/**
 * Country coordinates for pin placement on world map
 * Coordinates use standard geographic coordinates (latitude, longitude)
 * Latitude: -90 (South) to +90 (North)
 * Longitude: -180 (West) to +180 (East)
 */

export interface CountryCoordinate {
  country: string;
  latitude: number;
  longitude: number;
}

export const countryCoordinates: Record<string, CountryCoordinate> = {
  // Africa
  Egypt: { country: 'Egypt', latitude: 26.8206, longitude: 30.8025 }, // Cairo
  Tanzania: { country: 'Tanzania', latitude: -6.7924, longitude: 39.2083 }, // Dar es Salaam
  Kenya: { country: 'Kenya', latitude: -1.2921, longitude: 36.8219 }, // Nairobi
  Morocco: { country: 'Morocco', latitude: 33.5731, longitude: -7.5898 }, // Casablanca
  'South Africa': { country: 'South Africa', latitude: -33.9249, longitude: 18.4241 }, // Cape Town
  Tunisia: { country: 'Tunisia', latitude: 36.8065, longitude: 10.1815 }, // Tunis
  Ethiopia: { country: 'Ethiopia', latitude: 9.0320, longitude: 38.7469 }, // Addis Ababa
  Madagascar: { country: 'Madagascar', latitude: -18.8792, longitude: 47.5079 }, // Antananarivo
  Mauritius: { country: 'Mauritius', latitude: -20.1609, longitude: 57.5012 }, // Port Louis
  Seychelles: { country: 'Seychelles', latitude: -4.6796, longitude: 55.4920 }, // Victoria
  
  // Asia - East & Southeast
  Bali: { country: 'Bali', latitude: -8.4095, longitude: 115.1889 }, // Bali, Indonesia
  China: { country: 'China', latitude: 39.9042, longitude: 116.4074 }, // Beijing
  India: { country: 'India', latitude: 28.6139, longitude: 77.2090 }, // New Delhi
  Malaysia: { country: 'Malaysia', latitude: 3.1390, longitude: 101.6869 }, // Kuala Lumpur
  Nepal: { country: 'Nepal', latitude: 27.7172, longitude: 85.3240 }, // Kathmandu
  Singapore: { country: 'Singapore', latitude: 1.3521, longitude: 103.8198 }, // Singapore
  Thailand: { country: 'Thailand', latitude: 13.7563, longitude: 100.5018 }, // Bangkok
  Vietnam: { country: 'Vietnam', latitude: 21.0285, longitude: 105.8542 }, // Hanoi
  Indonesia: { country: 'Indonesia', latitude: -6.2088, longitude: 106.8456 }, // Jakarta
  Philippines: { country: 'Philippines', latitude: 14.5995, longitude: 120.9842 }, // Manila
  Cambodia: { country: 'Cambodia', latitude: 11.5564, longitude: 104.9282 }, // Phnom Penh
  Laos: { country: 'Laos', latitude: 17.9757, longitude: 102.6331 }, // Vientiane
  Myanmar: { country: 'Myanmar', latitude: 16.8661, longitude: 96.1951 }, // Yangon
  Japan: { country: 'Japan', latitude: 35.6762, longitude: 139.6503 }, // Tokyo
  'South Korea': { country: 'South Korea', latitude: 37.5665, longitude: 126.9780 }, // Seoul
  Taiwan: { country: 'Taiwan', latitude: 25.0330, longitude: 121.5654 }, // Taipei
  'Hong Kong': { country: 'Hong Kong', latitude: 22.3193, longitude: 114.1694 }, // Hong Kong
  Maldives: { country: 'Maldives', latitude: 4.1755, longitude: 73.5093 }, // Male
  'Sri Lanka': { country: 'Sri Lanka', latitude: 6.9271, longitude: 79.8612 }, // Colombo
  
  // Asia - Central & South
  Bhutan: { country: 'Bhutan', latitude: 27.4728, longitude: 89.6393 }, // Thimphu
  Pakistan: { country: 'Pakistan', latitude: 33.6844, longitude: 73.0479 }, // Islamabad
  Bangladesh: { country: 'Bangladesh', latitude: 23.8103, longitude: 90.4125 }, // Dhaka
  Afghanistan: { country: 'Afghanistan', latitude: 34.5553, longitude: 69.2075 }, // Kabul
  Uzbekistan: { country: 'Uzbekistan', latitude: 41.2995, longitude: 69.2401 }, // Tashkent
  Kazakhstan: { country: 'Kazakhstan', latitude: 51.1694, longitude: 71.4491 }, // Nur-Sultan
  Mongolia: { country: 'Mongolia', latitude: 47.8864, longitude: 106.9057 }, // Ulaanbaatar
  
  // Middle East
  Jordan: { country: 'Jordan', latitude: 31.9454, longitude: 35.9284 }, // Amman
  Oman: { country: 'Oman', latitude: 23.5880, longitude: 58.3829 }, // Muscat
  'United Arab Emirates': { country: 'United Arab Emirates', latitude: 25.2048, longitude: 55.2708 }, // Dubai
  Dubai: { country: 'Dubai', latitude: 25.2048, longitude: 55.2708 }, // Dubai
  Israel: { country: 'Israel', latitude: 31.7683, longitude: 35.2137 }, // Jerusalem
  Lebanon: { country: 'Lebanon', latitude: 33.8886, longitude: 35.4955 }, // Beirut
  Turkey: { country: 'Turkey', latitude: 41.0082, longitude: 28.9784 }, // Istanbul
  'Saudi Arabia': { country: 'Saudi Arabia', latitude: 24.7136, longitude: 46.6753 }, // Riyadh
  Qatar: { country: 'Qatar', latitude: 25.2854, longitude: 51.5310 }, // Doha
  Kuwait: { country: 'Kuwait', latitude: 29.3759, longitude: 47.9774 }, // Kuwait City
  Bahrain: { country: 'Bahrain', latitude: 26.0667, longitude: 50.5577 }, // Manama
  Iran: { country: 'Iran', latitude: 35.6892, longitude: 51.3890 }, // Tehran
  Iraq: { country: 'Iraq', latitude: 33.3152, longitude: 44.3661 }, // Baghdad
  
  // Europe - Western
  France: { country: 'France', latitude: 48.8566, longitude: 2.3522 }, // Paris
  Spain: { country: 'Spain', latitude: 40.4168, longitude: -3.7038 }, // Madrid
  Italy: { country: 'Italy', latitude: 41.9028, longitude: 12.4964 }, // Rome
  Germany: { country: 'Germany', latitude: 52.5200, longitude: 13.4050 }, // Berlin
  'United Kingdom': { country: 'United Kingdom', latitude: 51.5074, longitude: -0.1278 }, // London
  Portugal: { country: 'Portugal', latitude: 38.7223, longitude: -9.1393 }, // Lisbon
  Netherlands: { country: 'Netherlands', latitude: 52.3676, longitude: 4.9041 }, // Amsterdam
  Belgium: { country: 'Belgium', latitude: 50.8503, longitude: 4.3517 }, // Brussels
  Switzerland: { country: 'Switzerland', latitude: 46.9480, longitude: 7.4474 }, // Bern
  Austria: { country: 'Austria', latitude: 48.2082, longitude: 16.3738 }, // Vienna
  Ireland: { country: 'Ireland', latitude: 53.3498, longitude: -6.2603 }, // Dublin
  Luxembourg: { country: 'Luxembourg', latitude: 49.6116, longitude: 6.1319 }, // Luxembourg
  
  // Europe - Eastern
  Greece: { country: 'Greece', latitude: 37.9838, longitude: 23.7275 }, // Athens
  Poland: { country: 'Poland', latitude: 52.2297, longitude: 21.0122 }, // Warsaw
  Romania: { country: 'Romania', latitude: 44.4268, longitude: 26.1025 }, // Bucharest
  'Czech Republic': { country: 'Czech Republic', latitude: 50.0755, longitude: 14.4378 }, // Prague
  Hungary: { country: 'Hungary', latitude: 47.4979, longitude: 19.0402 }, // Budapest
  Bulgaria: { country: 'Bulgaria', latitude: 42.6977, longitude: 23.3219 }, // Sofia
  Croatia: { country: 'Croatia', latitude: 45.8150, longitude: 15.9819 }, // Zagreb
  Serbia: { country: 'Serbia', latitude: 44.7866, longitude: 20.4489 }, // Belgrade
  Ukraine: { country: 'Ukraine', latitude: 50.4501, longitude: 30.5234 }, // Kyiv
  Russia: { country: 'Russia', latitude: 55.7558, longitude: 37.6173 }, // Moscow
  
  // Europe - Northern
  Norway: { country: 'Norway', latitude: 59.9139, longitude: 10.7522 }, // Oslo
  Sweden: { country: 'Sweden', latitude: 59.3293, longitude: 18.0686 }, // Stockholm
  Finland: { country: 'Finland', latitude: 60.1695, longitude: 24.9354 }, // Helsinki
  Denmark: { country: 'Denmark', latitude: 55.6761, longitude: 12.5683 }, // Copenhagen
  Iceland: { country: 'Iceland', latitude: 64.1466, longitude: -21.9426 }, // Reykjavik
  
  // Americas - North
  'United States': { country: 'United States', latitude: 38.9072, longitude: -77.0369 }, // Washington DC
  Canada: { country: 'Canada', latitude: 45.4215, longitude: -75.6972 }, // Ottawa
  Mexico: { country: 'Mexico', latitude: 19.4326, longitude: -99.1332 }, // Mexico City
  Cuba: { country: 'Cuba', latitude: 23.1136, longitude: -82.3666 }, // Havana
  Jamaica: { country: 'Jamaica', latitude: 18.0179, longitude: -76.8099 }, // Kingston
  'Costa Rica': { country: 'Costa Rica', latitude: 9.9281, longitude: -84.0907 }, // San Jose
  Panama: { country: 'Panama', latitude: 8.9824, longitude: -79.5199 }, // Panama City
  
  // Americas - South
  Brazil: { country: 'Brazil', latitude: -15.7975, longitude: -47.8919 }, // Brasilia
  Argentina: { country: 'Argentina', latitude: -34.6037, longitude: -58.3816 }, // Buenos Aires
  Chile: { country: 'Chile', latitude: -33.4489, longitude: -70.6693 }, // Santiago
  Peru: { country: 'Peru', latitude: -12.0464, longitude: -77.0428 }, // Lima
  Colombia: { country: 'Colombia', latitude: 4.7110, longitude: -74.0721 }, // Bogota
  Ecuador: { country: 'Ecuador', latitude: -0.1807, longitude: -78.4678 }, // Quito
  Bolivia: { country: 'Bolivia', latitude: -16.5000, longitude: -68.1500 }, // La Paz
  Venezuela: { country: 'Venezuela', latitude: 10.4806, longitude: -66.9036 }, // Caracas
  Uruguay: { country: 'Uruguay', latitude: -34.9011, longitude: -56.1645 }, // Montevideo
  Paraguay: { country: 'Paraguay', latitude: -25.2637, longitude: -57.5759 }, // Asuncion
  
  // Oceania
  Australia: { country: 'Australia', latitude: -33.8688, longitude: 151.2093 }, // Sydney
  'New Zealand': { country: 'New Zealand', latitude: -41.2865, longitude: 174.7762 }, // Wellington
  Fiji: { country: 'Fiji', latitude: -18.1416, longitude: 178.4419 }, // Suva
  'Papua New Guinea': { country: 'Papua New Guinea', latitude: -9.4438, longitude: 147.1803 }, // Port Moresby
};

/**
 * Get coordinates for a country
 * @param countryName - Name of the country
 * @returns CountryCoordinate or null if not found
 */
export const getCountryCoordinates = (countryName: string): CountryCoordinate | null => {
  return countryCoordinates[countryName] || null;
};

/**
 * Get all country names that have coordinates
 */
export const getAvailableCountries = (): string[] => {
  return Object.keys(countryCoordinates);
};

